import React, { ReactNode } from "react";
import Cookies from "js-cookie";
import classNames from "classnames";
import throttle from "lodash.throttle";

import { ReaderContext } from "@PublicationReader/context";
import { Tooltip } from "@PublicationReader/components/Tooltip";
import { SectionI } from "@PublicationReader/@types/Publication";

interface ProgressBarProps {
  sections: SectionI[];
}

interface ProgressBarSectionAnchorProps {
  progressPercentage: number;
  section: SectionI;
}

const ProgressBarSectionAnchor = ({
  section,
  progressPercentage,
}: ProgressBarSectionAnchorProps) => {
  const { documentHeight, setActiveSectionId } =
    React.useContext(ReaderContext);
  const sectionElement = document.getElementById(section.sectionId);
  const [position, setPosition] = React.useState(0);

  React.useEffect(() => {
    const pos = sectionElement
      ? ((sectionElement.offsetTop - 50) / documentHeight) * 100
      : 0;
    setPosition(pos);
  }, [sectionElement, documentHeight]);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();

    if (sectionElement) {
      window.scroll({
        top: sectionElement.offsetTop - 50,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  React.useEffect(() => {
    if (
      progressPercentage !== 0 &&
      position !== 0 &&
      progressPercentage >= position
    ) {
      setActiveSectionId(section.sectionId);
    }
  }, [position, progressPercentage, setActiveSectionId]);

  return (
    // Don't show indicator dots for subsections
    section.headingLevel === null ? (
      <>
        <button
          className={classNames(
            "pubr-progress-bar__indicator-anchor",
            progressPercentage >= position &&
              "pubr-progress-bar__indicator-anchor--complete",
          )}
          style={{ left: `${position}%` }}
          data-tooltip-id={section.sectionId}
          data-tooltip-place="top"
          data-tooltip-content={section.sectionHeading}
          onClick={(event) => handleClick(event)}
          aria-label={"Add aria-label for en and sv"}
        ></button>
        <Tooltip id={section.sectionId} />
      </>
    ) : null
  );
};

const RecursiveProgressBarSection = ({
  section,
  progressPercentage,
}: {
  section: SectionI;
  progressPercentage: number;
}) => (
  <>
    <ProgressBarSectionAnchor
      section={section}
      progressPercentage={progressPercentage}
      key={section.sectionId}
    />
    {section.subSections &&
      section.subSections.length > 0 &&
      section.subSections.map((sub) => (
        <RecursiveProgressBarSection
          section={sub}
          progressPercentage={progressPercentage}
          key={sub.sectionId}
        />
      ))}
  </>
);

const ProgressBar = ({ sections }: ProgressBarProps) => {
  const { documentHeight } = React.useContext(ReaderContext);
  const [progressPercentage, setProgressPercentage] = React.useState(0);

  const handleScroll = throttle(() => {
    const scrollY =
      document.documentElement.scrollTop || document.body.scrollTop || 0;
    const percentage = (scrollY / documentHeight) * 100;

    if (scrollY + window.innerHeight >= documentHeight) {
      setProgressPercentage(100);
    } else {
      setProgressPercentage(percentage);
    }

    if (percentage > 5) {
      Cookies.set("progress", String(scrollY));
    }
  }, 100);

  React.useEffect(() => {
    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className="pubr-progress-bar">
      <div
        className="pubr-progress-bar__indicator"
        style={{ width: `${progressPercentage}%` }}
      >
        {sections.map((s) => (
          <RecursiveProgressBarSection
            section={s}
            progressPercentage={progressPercentage}
            key={s.sectionId}
          />
        ))}
      </div>
    </div>
  );
};

export { ProgressBar };
